import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import './ComplaintCategoryManagement.css';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import ListSearchBar from '../../../shared/components/common/ListSearchBar';
import SortableTh from '../../../shared/components/common/SortableTh';
import { useServerTableSort } from '../../../shared/utils/serverTableSort';
import { toBoolean } from '../../../utils/booleanUtils';

const createEmptyCategoryForm = () => ({
  code: '',
  label: '',
  sort_order: 0,
  is_active: true,
  applies_to_all_locations: true,
  applies_to_all_device_names: true,
  location_ids: [],
  device_name_ids: [],
});

/**
 * Shared form fields for both create + edit modals.
 * Locks code / is_active / applicability when editing a system category.
 */
function CategoryFormFields({ data, onChange, isSystem, locations, deviceNames }) {
  const [locationSearch, setLocationSearch] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');

  const setField = (field, value) => onChange((prev) => ({ ...prev, [field]: value }));

  const toggleId = (field, id) => {
    onChange((prev) => {
      const current = prev[field] || [];
      const next = current.includes(id)
        ? current.filter((existingId) => existingId !== id)
        : [...current, id];
      return { ...prev, [field]: next };
    });
  };

  const filteredLocations = locations.filter((location) =>
    !locationSearch.trim() ||
    (location.name || '').toLowerCase().includes(locationSearch.trim().toLowerCase())
  );

  const filteredDeviceNames = deviceNames.filter((deviceName) =>
    !deviceSearch.trim() ||
    (deviceName.name || '').toLowerCase().includes(deviceSearch.trim().toLowerCase()) ||
    (deviceName.code || '').toLowerCase().includes(deviceSearch.trim().toLowerCase())
  );

  return (
    <div className="ccm-form-grid">
      <div className="ccm-form-row-2">
        <div className="ccm-form-group">
          <label htmlFor="category-code">Code *</label>
          <input
            id="category-code"
            type="text"
            value={data.code}
            onChange={(e) => setField('code', e.target.value)}
            placeholder="e.g. power_failure"
            disabled={isSystem}
            required
            autoFocus={!isSystem}
          />
          {isSystem && <small>System category code cannot be changed</small>}
        </div>

        <div className="ccm-form-group">
          <label htmlFor="category-sort">Sort Order</label>
          <input
            id="category-sort"
            type="number"
            value={data.sort_order}
            onChange={(e) => setField('sort_order', e.target.value)}
            placeholder="0"
          />
        </div>
      </div>

      <div className="ccm-form-group">
        <label htmlFor="category-label">Label *</label>
        <input
          id="category-label"
          type="text"
          value={data.label}
          onChange={(e) => setField('label', e.target.value)}
          placeholder="e.g. Power Failure"
          required
        />
      </div>

      <div className={`ccm-status-toggle ${isSystem ? 'is-disabled' : ''}`}>
        <div className="ccm-status-toggle-copy">
          <span className="ccm-status-toggle-title">Active</span>
          <span className="ccm-status-toggle-hint">
            {isSystem
              ? 'System categories stay active'
              : 'Inactive categories are hidden from new complaints'}
          </span>
        </div>
        <button
          type="button"
          className={`ccm-switch ${data.is_active ? 'on' : ''}`}
          onClick={() => !isSystem && setField('is_active', !data.is_active)}
          disabled={isSystem}
          aria-pressed={!!data.is_active}
          aria-label={data.is_active ? 'Active' : 'Inactive'}
        >
          <span className="ccm-switch-knob" />
        </button>
      </div>

      <div className="ccm-applicability">
        <div className="ccm-applicability-header">
          <span className="ccm-applicability-title">Applies to locations</span>
          <div className="ccm-segmented">
            <button
              type="button"
              className={data.applies_to_all_locations ? 'ccm-segment active' : 'ccm-segment'}
              onClick={() => setField('applies_to_all_locations', true)}
              disabled={isSystem}
            >
              All
            </button>
            <button
              type="button"
              className={!data.applies_to_all_locations ? 'ccm-segment active' : 'ccm-segment'}
              onClick={() => setField('applies_to_all_locations', false)}
              disabled={isSystem}
            >
              Specific
            </button>
          </div>
        </div>
        {data.applies_to_all_locations ? (
          <p className="ccm-applicability-help">Includes current and future locations.</p>
        ) : (
          <div className="ccm-picker">
            <input
              type="search"
              className="ccm-picker-search"
              placeholder="Search locations…"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
            />
            <div className="ccm-multiselect">
              {filteredLocations.length === 0 && (
                <div className="ccm-multiselect-empty">No locations match</div>
              )}
              {filteredLocations.map((location) => (
                <label key={location.id} className="ccm-multiselect-item">
                  <input
                    type="checkbox"
                    checked={(data.location_ids || []).includes(location.id)}
                    onChange={() => toggleId('location_ids', location.id)}
                  />
                  <span>{location.name}</span>
                </label>
              ))}
            </div>
            <p className="ccm-applicability-help">
              {(data.location_ids || []).length} selected · new locations are not included automatically
            </p>
          </div>
        )}
      </div>

      <div className="ccm-applicability">
        <div className="ccm-applicability-header">
          <span className="ccm-applicability-title">Applies to device names</span>
          <div className="ccm-segmented">
            <button
              type="button"
              className={data.applies_to_all_device_names ? 'ccm-segment active' : 'ccm-segment'}
              onClick={() => setField('applies_to_all_device_names', true)}
              disabled={isSystem}
            >
              All
            </button>
            <button
              type="button"
              className={!data.applies_to_all_device_names ? 'ccm-segment active' : 'ccm-segment'}
              onClick={() => setField('applies_to_all_device_names', false)}
              disabled={isSystem}
            >
              Specific
            </button>
          </div>
        </div>
        {data.applies_to_all_device_names ? (
          <p className="ccm-applicability-help">Includes current and future device names.</p>
        ) : (
          <div className="ccm-picker">
            <input
              type="search"
              className="ccm-picker-search"
              placeholder="Search device names…"
              value={deviceSearch}
              onChange={(e) => setDeviceSearch(e.target.value)}
            />
            <div className="ccm-multiselect">
              {filteredDeviceNames.length === 0 && (
                <div className="ccm-multiselect-empty">No device names match</div>
              )}
              {filteredDeviceNames.map((deviceName) => (
                <label key={deviceName.id} className="ccm-multiselect-item">
                  <input
                    type="checkbox"
                    checked={(data.device_name_ids || []).includes(deviceName.id)}
                    onChange={() => toggleId('device_name_ids', deviceName.id)}
                  />
                  <span>
                    {deviceName.name}
                    {deviceName.code ? <em className="ccm-picker-code"> ({deviceName.code})</em> : null}
                    {deviceName.is_active !== undefined && !toBoolean(deviceName.is_active) ? (
                      <span className="ccm-status ccm-status-inactive"> Inactive</span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
            <p className="ccm-applicability-help">
              {(data.device_name_ids || []).length} selected · new device names are not included automatically
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function validateCategoryForm(data) {
  if (!data.code || !data.code.trim()) {
    return 'Code is required';
  }
  if (!data.label || !data.label.trim()) {
    return 'Label is required';
  }
  if (!data.applies_to_all_locations && (data.location_ids || []).length === 0) {
    return 'Select at least one location, or choose "All Locations"';
  }
  if (!data.applies_to_all_device_names && (data.device_name_ids || []).length === 0) {
    return 'Select at least one device name, or choose "All Device Names"';
  }
  return null;
}

function buildCategoryPayload(data) {
  return {
    code: data.code.trim(),
    label: data.label.trim(),
    sort_order: parseInt(data.sort_order, 10) || 0,
    is_active: !!data.is_active,
    applies_to_all_locations: !!data.applies_to_all_locations,
    applies_to_all_device_names: !!data.applies_to_all_device_names,
    location_ids: data.applies_to_all_locations ? [] : (data.location_ids || []),
    device_name_ids: data.applies_to_all_device_names ? [] : (data.device_name_ids || []),
  };
}

function ComplaintCategoryManagement({ token }) {
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [deviceNames, setDeviceNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const { sorting, handleSortChange } = useServerTableSort('sort_order', 'asc');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState(createEmptyCategoryForm());
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState(createEmptyCategoryForm());
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBlockedInfo, setDeleteBlockedInfo] = useState(null);
  const [scopeDetailsCategory, setScopeDetailsCategory] = useState(null);
  const [scopeDetailsSearch, setScopeDetailsSearch] = useState('');

  const openScopeDetails = (category) => {
    setScopeDetailsSearch('');
    setScopeDetailsCategory(category);
  };

  const closeScopeDetails = () => {
    setScopeDetailsCategory(null);
    setScopeDetailsSearch('');
  };

  useEffect(() => {
    fetchCategories();
    fetchLocations();
    fetchDeviceNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent body scroll when any modal is open
  useEffect(() => {
    const anyModalOpen =
      showCreateModal ||
      !!editingCategory ||
      showDeleteConfirmation ||
      !!deleteBlockedInfo ||
      !!scopeDetailsCategory;
    document.body.style.overflow = anyModalOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCreateModal, editingCategory, showDeleteConfirmation, deleteBlockedInfo, scopeDetailsCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.COMPLAINT_CATEGORIES.LIST}?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching complaint categories:', error);
      toast.error('Failed to fetch complaint categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOCATIONS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  const fetchDeviceNames = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEVICE_NAMES.LIST_ALL, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDeviceNames(response.data?.device_names || []);
    } catch (error) {
      console.error('Error fetching device names:', error);
      setDeviceNames([]);
    }
  };

  const handleOpenCreate = () => {
    setCreateFormData(createEmptyCategoryForm());
    setCreateError('');
    setShowCreateModal(true);
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setCreateError('');
    setCreateFormData(createEmptyCategoryForm());
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const validationError = validateCategoryForm(createFormData);
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    setCreateError('');
    setIsCreating(true);
    try {
      await axios.post(API_ENDPOINTS.COMPLAINT_CATEGORIES.CREATE, buildCategoryPayload(createFormData), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Complaint category "${createFormData.label}" created successfully!`);
      setShowCreateModal(false);
      setCreateFormData(createEmptyCategoryForm());
      fetchCategories();
    } catch (error) {
      setCreateError(error.response?.data?.error || 'An unexpected error occurred while creating the category');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      code: category.code || '',
      label: category.label || '',
      sort_order: category.sort_order ?? 0,
      is_active: !!category.is_active,
      applies_to_all_locations: !!category.applies_to_all_locations,
      applies_to_all_device_names: !!category.applies_to_all_device_names,
      location_ids: category.location_ids || [],
      device_name_ids: category.device_name_ids || [],
    });
    setEditError('');
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setFormData(createEmptyCategoryForm());
    setEditError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const validationError = validateCategoryForm(formData);
    if (validationError) {
      setEditError(validationError);
      return;
    }

    setEditError('');
    setIsSaving(true);
    try {
      await axios.put(
        API_ENDPOINTS.COMPLAINT_CATEGORIES.UPDATE(editingCategory.id),
        buildCategoryPayload(formData),
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Complaint category updated successfully!');
      setEditingCategory(null);
      setFormData(createEmptyCategoryForm());
      fetchCategories();
    } catch (error) {
      setEditError(error.response?.data?.error || 'Failed to update complaint category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setShowDeleteConfirmation(false);
    setCategoryToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(API_ENDPOINTS.COMPLAINT_CATEGORIES.DELETE(categoryToDelete.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Complaint category deleted successfully!');
      setShowDeleteConfirmation(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;

      if (status === 400 && data?.usage_count != null) {
        setShowDeleteConfirmation(false);
        setDeleteBlockedInfo({ ...data, label: categoryToDelete.label });
      } else {
        toast.error(data?.error || 'Failed to delete complaint category');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseBlockedInfo = () => {
    setDeleteBlockedInfo(null);
    setCategoryToDelete(null);
  };

  const getSearchFilteredCategories = () => {
    if (!searchTerm) return categories;
    const searchLower = searchTerm.toLowerCase();
    return categories.filter((c) =>
      (c.code && c.code.toLowerCase().includes(searchLower)) ||
      (c.label && c.label.toLowerCase().includes(searchLower))
    );
  };

  const handleStatusFilterClick = (status) => {
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const handleTotalFilterClick = () => {
    setStatusFilter(null);
  };

  const formatScopePart = (allFlag, ids, allLabel, unit) => {
    if (allFlag) return allLabel;
    const count = (ids || []).length;
    return `${count} ${unit}${count === 1 ? '' : 's'}`;
  };

  const resolveScopedItems = (ids, catalog, getLabel) => {
    const idSet = new Set((ids || []).map((id) => String(id)));
    const found = catalog
      .filter((item) => idSet.has(String(item.id)))
      .map((item) => ({
        id: item.id,
        label: getLabel(item),
        inactive: item.is_active !== undefined && !toBoolean(item.is_active),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const foundIds = new Set(found.map((item) => String(item.id)));
    const missing = (ids || [])
      .filter((id) => !foundIds.has(String(id)))
      .map((id) => ({ id, label: `Unknown (#${id})`, inactive: false }));

    return [...found, ...missing];
  };

  const formatUpdatedAt = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      title: date.toLocaleString(),
    };
  };

  const compareCategories = (a, b) => {
    const dir = sorting.sort_direction === 'asc' ? 1 : -1;
    const getValue = (category) => {
      switch (sorting.sort_by) {
        case 'label':
          return (category.label || '').toLowerCase();
        case 'status':
          return toBoolean(category.is_active) ? 1 : 0;
        case 'usage_count':
          return Number(category.usage_count) || 0;
        case 'updated_at':
          return category.updated_at ? new Date(category.updated_at).getTime() : 0;
        case 'sort_order':
        default:
          return Number(category.sort_order) || 0;
      }
    };

    const aValue = getValue(a);
    const bValue = getValue(b);
    if (aValue < bValue) return -1 * dir;
    if (aValue > bValue) return 1 * dir;

    // Stable secondary sort by label, then id
    const labelCmp = (a.label || '').localeCompare(b.label || '');
    if (labelCmp !== 0) return labelCmp;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  };

  if (loading) {
    return <div className="ccm-loading">Loading complaint categories...</div>;
  }

  const searchFilteredCategories = getSearchFilteredCategories();
  const statusCounts = searchFilteredCategories.reduce(
    (acc, category) => {
      if (toBoolean(category.is_active)) {
        acc.active += 1;
      } else {
        acc.inactive += 1;
      }
      return acc;
    },
    { active: 0, inactive: 0 }
  );

  let filteredCategories = searchFilteredCategories;
  if (statusFilter) {
    filteredCategories = filteredCategories.filter((category) => {
      const isActive = toBoolean(category.is_active);
      return statusFilter === 'active' ? isActive : !isActive;
    });
  }
  filteredCategories = [...filteredCategories].sort(compareCategories);

  return (
    <div className="complaint-category-management">
      <div className="complaint-categories-description-section">
        <h1>📋 Complaint Categories</h1>
        <p>
          Org catalog for complaint logging — map to locations and device names.
        </p>
      </div>

      <div className="ccm-toolbar">
        <div className="ccm-stats-block">
          <div className="ccm-stats" aria-live="polite">
            <button
              type="button"
              className={`ccm-summary${!statusFilter ? ' is-active' : ''}`}
              onClick={handleTotalFilterClick}
              title="Clear filters"
              aria-pressed={!statusFilter}
            >
              <span className="ccm-summary-count">{searchFilteredCategories.length}</span>
              <span className="ccm-summary-label">{searchTerm ? 'Results' : 'Total'}</span>
            </button>
            <button
              type="button"
              className={`ccm-summary ccm-summary-active${statusFilter === 'active' ? ' is-active' : ''}`}
              onClick={() => handleStatusFilterClick('active')}
              title="Show active categories only"
              aria-pressed={statusFilter === 'active'}
            >
              <span className="ccm-summary-count">{statusCounts.active}</span>
              <span className="ccm-summary-label">Active</span>
            </button>
            <button
              type="button"
              className={`ccm-summary ccm-summary-inactive${statusFilter === 'inactive' ? ' is-active' : ''}`}
              onClick={() => handleStatusFilterClick('inactive')}
              title="Show inactive categories only"
              aria-pressed={statusFilter === 'inactive'}
            >
              <span className="ccm-summary-count">{statusCounts.inactive}</span>
              <span className="ccm-summary-label">Inactive</span>
            </button>
          </div>
          <p className="ccm-stats-help">
            Inactive categories stay on existing complaints but are hidden from new ones.
          </p>
        </div>
        <div className="action-palette">
          <button type="button" className="action-btn action-primary" onClick={handleOpenCreate}>
            <span className="action-icon">➕</span>
            <span className="action-text">Add Category</span>
          </button>
        </div>
      </div>

      <ListSearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={() => setSearchTerm('')}
        placeholder="Search by code or label…"
      />

      <div className="ccm-table-wrap">
        {filteredCategories.length > 0 ? (
          <table className="ccm-table">
            <thead>
              <tr>
                <SortableTh
                  column="label"
                  sorting={sorting}
                  onSortChange={handleSortChange}
                  className="ccm-col-category"
                >
                  Category
                </SortableTh>
                <SortableTh
                  column="status"
                  sorting={sorting}
                  onSortChange={handleSortChange}
                  className="ccm-col-status"
                >
                  Status
                </SortableTh>
                <th scope="col" className="ccm-col-scope">Scope</th>
                <SortableTh
                  column="usage_count"
                  sorting={sorting}
                  onSortChange={handleSortChange}
                  className="ccm-col-used"
                >
                  Used
                </SortableTh>
                <SortableTh
                  column="updated_at"
                  sorting={sorting}
                  onSortChange={handleSortChange}
                  className="ccm-col-updated"
                >
                  Updated
                </SortableTh>
                <th scope="col" className="ccm-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((category) => {
                const locationScope = formatScopePart(
                  category.applies_to_all_locations,
                  category.location_ids,
                  'All locations',
                  'location'
                );
                const deviceScope = formatScopePart(
                  category.applies_to_all_device_names,
                  category.device_name_ids,
                  'All device names',
                  'device name'
                );
                const locationSpecific = !category.applies_to_all_locations;
                const deviceSpecific = !category.applies_to_all_device_names;

                return (
                  <tr
                    key={category.id}
                    className={toBoolean(category.is_active) ? undefined : 'is-inactive'}
                  >
                    <td className="ccm-col-category">
                      <div className="ccm-category-cell">
                        <span className="ccm-category-label">{category.label}</span>
                        <span className="ccm-category-meta">
                          <code className="ccm-code">{category.code}</code>
                          {category.is_system && <span className="ccm-system-tag">System</span>}
                        </span>
                      </div>
                    </td>
                    <td className="ccm-col-status">
                      {toBoolean(category.is_active) ? (
                        <span className="ccm-status ccm-status-active">
                          <span className="ccm-status-icon" aria-hidden="true">✅</span>
                          Active
                        </span>
                      ) : (
                        <span className="ccm-status ccm-status-inactive">
                          <span className="ccm-status-icon" aria-hidden="true">⏸️</span>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="ccm-col-scope">
                      {locationSpecific || deviceSpecific ? (
                        <button
                          type="button"
                          className="ccm-scope ccm-scope-button"
                          onClick={() => openScopeDetails(category)}
                          title="View scope details"
                          aria-label={`View scope details for ${category.label}`}
                        >
                          <span className={locationSpecific ? 'ccm-scope-emphasis' : 'ccm-scope-muted'}>
                            {locationScope}
                          </span>
                          <span className="ccm-scope-sep" aria-hidden="true">·</span>
                          <span className={deviceSpecific ? 'ccm-scope-emphasis' : 'ccm-scope-muted'}>
                            {deviceScope}
                          </span>
                          <span className="ccm-scope-info" aria-hidden="true">i</span>
                        </button>
                      ) : (
                        <span className="ccm-scope">
                          <span className="ccm-scope-muted">{locationScope}</span>
                          <span className="ccm-scope-sep" aria-hidden="true">·</span>
                          <span className="ccm-scope-muted">{deviceScope}</span>
                        </span>
                      )}
                    </td>
                    <td className="ccm-col-used">
                      <span className="ccm-used">{category.usage_count ?? 0}</span>
                    </td>
                    <td className="ccm-col-updated">
                      {(() => {
                        const updated = formatUpdatedAt(category.updated_at);
                        if (!updated) return <span className="ccm-date-empty">—</span>;
                        return (
                          <div className="ccm-date-cell" title={updated.title}>
                            <span className="ccm-date-primary">{updated.date}</span>
                            <span className="ccm-date-secondary">{updated.time}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="ccm-col-actions">
                      <div className="ccm-row-actions">
                        <button
                          type="button"
                          className="ccm-row-btn"
                          onClick={() => handleEdit(category)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ccm-row-btn ccm-row-btn-danger"
                          onClick={() => handleDeleteClick(category)}
                          disabled={!!category.is_system}
                          title={
                            category.is_system
                              ? 'System categories cannot be deleted'
                              : 'Delete category'
                          }
                          aria-label={
                            category.is_system
                              ? 'Delete not allowed for system categories'
                              : `Delete ${category.label}`
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="ccm-empty">
            <p>
              {searchTerm || statusFilter
                ? 'No complaint categories found matching your filters.'
                : 'No complaint categories yet. Add one to get started.'}
            </p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && ReactDOM.createPortal(
        <div className="ccm-modal-overlay">
          <div className="ccm-modal">
            <div className="ccm-modal-header">
              <h3>Add Category</h3>
              <ModalCloseButton onClick={handleCancelCreate} title="Close" />
            </div>
            <form onSubmit={handleCreateCategory}>
              <div className="ccm-modal-body">
                {createError && <div className="ccm-error">{createError}</div>}
                <CategoryFormFields
                  data={createFormData}
                  onChange={setCreateFormData}
                  isSystem={false}
                  locations={locations}
                  deviceNames={deviceNames}
                />
              </div>
              <div className="ccm-modal-footer">
                <button type="button" className="action-btn action-secondary" onClick={handleCancelCreate} disabled={isCreating}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-primary" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Modal */}
      {editingCategory && ReactDOM.createPortal(
        <div className="ccm-modal-overlay">
          <div className="ccm-modal">
            <div className="ccm-modal-header">
              <h3>Edit Category</h3>
              <ModalCloseButton onClick={handleCancelEdit} title="Close" />
            </div>
            <form onSubmit={handleSave}>
              <div className="ccm-modal-body">
                {editError && <div className="ccm-error">{editError}</div>}
                <CategoryFormFields
                  data={formData}
                  onChange={setFormData}
                  isSystem={!!editingCategory.is_system}
                  locations={locations}
                  deviceNames={deviceNames}
                />
              </div>
              <div className="ccm-modal-footer">
                <button type="button" className="action-btn action-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-primary" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        entityName={categoryToDelete ? `${categoryToDelete.label} (${categoryToDelete.code})` : 'this category'}
        entityType="complaint category"
        action="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
        customMessage="You can delete this category only if it has never been used by a complaint."
      />

      {/* Delete Blocked Modal (In Use) */}
      {deleteBlockedInfo && ReactDOM.createPortal(
        <div className="ccm-modal-overlay">
          <div className="ccm-modal">
            <div className="ccm-modal-header">
              <h3>Cannot Delete Category</h3>
              <ModalCloseButton onClick={handleCloseBlockedInfo} title="Close" />
            </div>
            <div className="ccm-modal-body">
              <div className="ccm-error">
                “{deleteBlockedInfo.label}” is used by {deleteBlockedInfo.usage_count} complaint{deleteBlockedInfo.usage_count === 1 ? '' : 's'} and cannot be deleted.
              </div>
              <p className="ccm-hint">
                Deactivate this category instead — edit it and uncheck "Active" to hide it from new complaints
                while keeping historical data intact.
              </p>
            </div>
            <div className="ccm-modal-footer">
              <button type="button" className="action-btn action-primary" onClick={handleCloseBlockedInfo}>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Scope details (read-only) */}
      {scopeDetailsCategory && (() => {
        const scopeSearch = scopeDetailsSearch.trim().toLowerCase();
        const hasSpecificLocations = !scopeDetailsCategory.applies_to_all_locations;
        const hasSpecificDevices = !scopeDetailsCategory.applies_to_all_device_names;
        const showScopeSearch = hasSpecificLocations || hasSpecificDevices;

        const locationItems = hasSpecificLocations
          ? resolveScopedItems(
              scopeDetailsCategory.location_ids,
              locations,
              (location) => location.name || `Location #${location.id}`
            )
          : [];
        const deviceItems = hasSpecificDevices
          ? resolveScopedItems(
              scopeDetailsCategory.device_name_ids,
              deviceNames,
              (deviceName) =>
                `${deviceName.name || 'Unnamed'}${deviceName.code ? ` (${deviceName.code})` : ''}`
            )
          : [];

        const matchesScopeSearch = (item) =>
          !scopeSearch || (item.label || '').toLowerCase().includes(scopeSearch);

        const filteredLocations = locationItems.filter(matchesScopeSearch);
        const filteredDevices = deviceItems.filter(matchesScopeSearch);

        return ReactDOM.createPortal(
          <div className="ccm-modal-overlay" onClick={closeScopeDetails}>
            <div
              className="ccm-modal ccm-modal-scope"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="ccm-scope-details-title"
            >
              <div className="ccm-modal-header">
                <h3 id="ccm-scope-details-title">Scope — {scopeDetailsCategory.label}</h3>
                <ModalCloseButton onClick={closeScopeDetails} title="Close" />
              </div>
              <div className="ccm-modal-body">
                <div className="ccm-scope-details">
                  {showScopeSearch && (
                    <div className="ccm-scope-search">
                      <span className="ccm-scope-search-icon" aria-hidden="true">🔍</span>
                      <input
                        type="text"
                        value={scopeDetailsSearch}
                        onChange={(e) => setScopeDetailsSearch(e.target.value)}
                        placeholder="Search locations or device names…"
                        aria-label="Search scope items"
                        autoFocus
                      />
                      {scopeDetailsSearch && (
                        <button
                          type="button"
                          className="ccm-scope-search-clear"
                          onClick={() => setScopeDetailsSearch('')}
                          aria-label="Clear search"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}

                  <section className="ccm-scope-details-section">
                    {scopeDetailsCategory.applies_to_all_locations ? (
                      <>
                        <h4>Locations</h4>
                        <p className="ccm-hint">All locations (current and future)</p>
                      </>
                    ) : (
                      <>
                        <h4>
                          Locations
                          <span className="ccm-scope-details-count">
                            {scopeSearch
                              ? `${filteredLocations.length}/${locationItems.length}`
                              : locationItems.length}
                          </span>
                        </h4>
                        {filteredLocations.length > 0 ? (
                          <div className="ccm-scope-chips">
                            {filteredLocations.map((item) => (
                              <span
                                key={item.id}
                                className={`ccm-scope-chip${item.inactive ? ' is-inactive' : ''}`}
                                title={item.inactive ? `${item.label} (inactive)` : item.label}
                              >
                                <span className="ccm-scope-chip-label">{item.label}</span>
                                {item.inactive && (
                                  <span className="ccm-scope-chip-tag">Inactive</span>
                                )}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="ccm-scope-search-empty">
                            No locations match “{scopeDetailsSearch.trim()}”.
                          </p>
                        )}
                      </>
                    )}
                  </section>

                  <section className="ccm-scope-details-section">
                    {scopeDetailsCategory.applies_to_all_device_names ? (
                      <>
                        <h4>Device names</h4>
                        <p className="ccm-hint">All device names (current and future)</p>
                      </>
                    ) : (
                      <>
                        <h4>
                          Device names
                          <span className="ccm-scope-details-count">
                            {scopeSearch
                              ? `${filteredDevices.length}/${deviceItems.length}`
                              : deviceItems.length}
                          </span>
                        </h4>
                        {filteredDevices.length > 0 ? (
                          <div className="ccm-scope-chips">
                            {filteredDevices.map((item) => (
                              <span
                                key={item.id}
                                className={`ccm-scope-chip${item.inactive ? ' is-inactive' : ''}`}
                                title={item.inactive ? `${item.label} (inactive)` : item.label}
                              >
                                <span className="ccm-scope-chip-label">{item.label}</span>
                                {item.inactive && (
                                  <span className="ccm-scope-chip-tag">Inactive</span>
                                )}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="ccm-scope-search-empty">
                            No device names match “{scopeDetailsSearch.trim()}”.
                          </p>
                        )}
                      </>
                    )}
                  </section>
                </div>
              </div>
              <div className="ccm-modal-footer">
                <button
                  type="button"
                  className="action-btn action-primary"
                  onClick={closeScopeDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
}

export default ComplaintCategoryManagement;
